import React, { useMemo, useState } from 'react';
import FdModal from '../FdModal';
import {
  formatCurrency,
  formatDate,
  todayIso,
  triggerCsvDownload,
  triggerExcelLikeDownload,
} from './utils';
import TablePagination, { usePagination } from '../TablePagination';

function createEmptyForm() {
  return {
    remittance_date: todayIso(),
    collection_date: todayIso(),
    cash_amount: '',
    check_amount: '',
    reference_no: '',
    notes: '',
    deposit_slip_name: '',
    deposit_slip_url: '',
  };
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function BankRemittanceTab({
  bankRemittances,
  collections,
  selectedBranch,
  isLoading,
  isSubmitting,
  onCreate,
  onUpdate,
  onCancel,
  onDelete,
  onUploadDepositSlip,
  canCreateRemittance,
  canUpdateRemittance,
  canCancelRemittance,
  canDeleteRemittance,
}) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(createEmptyForm());
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isUploadingSlip, setIsUploadingSlip] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const filteredRemittances = useMemo(() => {
    return bankRemittances.filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch);
  }, [bankRemittances, selectedBranch]);

  const activeRemittances = useMemo(
    () => filteredRemittances.filter((row) => String(row.status || '') !== 'Cancelled'),
    [filteredRemittances]
  );

  const pagination = usePagination(filteredRemittances);

  const paidCollections = useMemo(() => {
    return collections
      .filter((row) => String(row.status || '') === 'Paid')
      .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch);
  }, [collections, selectedBranch]);

  const reportRows = useMemo(() => {
    const dates = new Set();
    paidCollections.forEach((row) => dates.add(String(row.payment_date || row.date || '')));
    activeRemittances.forEach((row) => dates.add(String(row.collection_date || '')));

    return Array.from(dates)
      .filter((date) => date)
      .sort((left, right) => right.localeCompare(left))
      .map((date) => {
        const dayCollections = paidCollections.filter((row) => String(row.payment_date || row.date || '') === date);
        const dayRemittances = activeRemittances.filter((row) => String(row.collection_date || '') === date);

        const totalCollection = dayCollections.reduce((sum, row) => sum + Number(row.premium_amount || 0), 0);
        const totalRemitted = dayRemittances.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);

        return {
          date,
          collection_count: dayCollections.length,
          remittance_count: dayRemittances.length,
          total_collection: totalCollection,
          total_remitted: totalRemitted,
          discrepancy: totalRemitted - totalCollection,
        };
      });
  }, [paidCollections, activeRemittances]);

  const totals = useMemo(() => {
    const totalCollections = paidCollections.reduce((sum, row) => sum + Number(row.premium_amount || 0), 0);
    const totalRemitted = activeRemittances.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
    return {
      totalCollections,
      totalRemitted,
      discrepancy: totalRemitted - totalCollections,
    };
  }, [paidCollections, activeRemittances]);

  function openCreateModal() {
    if (!canCreateRemittance) {
      return;
    }

    setEditingRecord(null);
    setForm(createEmptyForm());
    setUploadError('');
    setIsFormOpen(true);
  }

  function openEditModal(record) {
    if (!canUpdateRemittance) {
      return;
    }

    setEditingRecord(record);
    setForm({
      remittance_date: record.remittance_date || '',
      collection_date: record.collection_date || '',
      cash_amount: String(record.cash_amount ?? ''),
      check_amount: String(record.check_amount ?? ''),
      reference_no: record.reference_no || '',
      notes: record.notes || '',
      deposit_slip_name: record.deposit_slip_name || '',
      deposit_slip_url: record.deposit_slip_url || '',
    });
    setUploadError('');
    setIsFormOpen(true);
  }

  async function handleDepositSlipChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || typeof onUploadDepositSlip !== 'function') {
      return;
    }

    setUploadError('');
    setIsUploadingSlip(true);

    try {
      const result = await onUploadDepositSlip(file);
      if (result?.name && result?.url) {
        setForm((p) => ({ ...p, deposit_slip_name: result.name, deposit_slip_url: result.url }));
      } else {
        setUploadError('Failed to upload deposit slip.');
      }
    } catch (err) {
      setUploadError(err?.message || 'Failed to upload deposit slip.');
    } finally {
      setIsUploadingSlip(false);
    }
  }

  function removeDepositSlip() {
    setForm((p) => ({ ...p, deposit_slip_name: '', deposit_slip_url: '' }));
  }

  async function submitForm() {
    if (editingRecord && !canUpdateRemittance) {
      return;
    }

    if (!editingRecord && !canCreateRemittance) {
      return;
    }

    const payload = {
      remittance_date: form.remittance_date,
      collection_date: form.collection_date,
      cash_amount: toNumber(form.cash_amount),
      check_amount: toNumber(form.check_amount),
      reference_no: form.reference_no || null,
      notes: form.notes || null,
      deposit_slip_name: form.deposit_slip_name || null,
      deposit_slip_url: form.deposit_slip_url || null,
      branch: selectedBranch !== 'all' ? selectedBranch : null,
    };

    const success = editingRecord
      ? await onUpdate(editingRecord.id, payload)
      : await onCreate(payload);

    if (success) {
      setIsFormOpen(false);
      setEditingRecord(null);
      setForm(createEmptyForm());
    }
  }

  async function submitCancel() {
    if (!canCancelRemittance) {
      return;
    }

    const success = await onCancel(cancelTarget.id, {
      cancellation_reason: cancelReason,
      cancelled_date: todayIso(),
    });

    if (success) {
      setCancelTarget(null);
      setCancelReason('');
    }
  }

  function exportRemittances() {
    triggerExcelLikeDownload(
      `remittances-${todayIso()}.xls`,
      ['Remittance Date', 'Collection Date', 'Branch', 'Reference No.', 'Cash', 'Check', 'Total', 'Deposit Slip', 'Status', 'Cancellation Reason', 'Notes'],
      filteredRemittances.map((row) => [
        formatDate(row.remittance_date),
        formatDate(row.collection_date),
        row.branch || '',
        row.reference_no || '',
        row.cash_amount ?? 0,
        row.check_amount ?? 0,
        row.total_amount ?? 0,
        row.deposit_slip_url || '',
        row.status || '',
        row.cancellation_reason || '',
        row.notes || '',
      ])
    );
  }

  function exportReport() {
    triggerCsvDownload(
      `collection-vs-remittance-${todayIso()}.csv`,
      ['Date', '# Collections', 'Total Collections', '# Remittances', 'Total Remitted', 'Discrepancy', 'Status'],
      reportRows.map((row) => [
        formatDate(row.date),
        row.collection_count,
        row.total_collection,
        row.remittance_count,
        row.total_remitted,
        row.discrepancy,
        row.discrepancy === 0 ? 'Balanced' : row.discrepancy > 0 ? 'Excess Deposit' : 'Shortage',
      ])
    );
  }

  return (
    <div className="collections-tab-content">
      <div className="collections-summary-grid">
        <div className="collections-summary-card">
          <p>Total Collections (Paid)</p>
          <strong>{formatCurrency(totals.totalCollections)}</strong>
        </div>
        <div className="collections-summary-card">
          <p>Total Bank Remittances</p>
          <strong>{formatCurrency(totals.totalRemitted)}</strong>
        </div>
        <div className={`collections-summary-card ${totals.discrepancy === 0 ? 'is-balanced' : 'is-warning'}`}>
          <p>Overall Discrepancy</p>
          <strong>{formatCurrency(Math.abs(totals.discrepancy))}</strong>
          <span>{totals.discrepancy === 0 ? 'Balanced' : totals.discrepancy > 0 ? 'Excess Deposit' : 'Shortage'}</span>
        </div>
      </div>

      <section className="collections-section-card">
        <header className="collections-section-header">
          <h3>Collection vs Remittance Report</h3>
          <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={exportReport}>
            Export
          </button>
        </header>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table collections-table">
            <thead>
              <tr>
                <th>Date</th>
                <th># Collections</th>
                <th>Total Collections</th>
                <th># Remittances</th>
                <th>Total Remitted</th>
                <th>Discrepancy</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reportRows.length === 0 ? (
                <tr><td colSpan={7} className="dashboard-empty">No report data yet.</td></tr>
              ) : reportRows.map((row) => (
                <tr key={row.date}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.collection_count}</td>
                  <td className="collections-number">{formatCurrency(row.total_collection)}</td>
                  <td>{row.remittance_count}</td>
                  <td className="collections-number">{formatCurrency(row.total_remitted)}</td>
                  <td className={`collections-number ${row.discrepancy === 0 ? '' : 'collections-danger'}`}>{formatCurrency(row.discrepancy)}</td>
                  <td>
                    <span className={`status-badge ${row.discrepancy === 0 ? 'status-active' : 'status-cancelled'}`}>
                      {row.discrepancy === 0 ? 'Balanced' : row.discrepancy > 0 ? 'Excess' : 'Shortage'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="collections-section-card">
        <header className="collections-section-header">
          <h3>Remittance Records</h3>
          <div className="collections-section-actions">
            <button type="button" className="profile-secondary-btn" onClick={exportRemittances}>
              Export
            </button>
            {canCreateRemittance ? (
              <button type="button" className="profile-secondary-btn" onClick={openCreateModal}>
                + New Remittance
              </button>
            ) : null}
          </div>
        </header>

        {isLoading ? (
          <div className="dashboard-loading">Loading remittances...</div>
        ) : filteredRemittances.length === 0 ? (
          <div className="dashboard-empty">No remittance records found.</div>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table collections-table">
              <thead>
                <tr>
                  <th>Remittance Date</th>
                  <th>Collection Date</th>
                  <th>Branch</th>
                  <th>Reference No.</th>
                  <th>Cash</th>
                  <th>Check</th>
                  <th>Total</th>
                  <th>Deposit Slip</th>
                  <th>Status</th>
                  <th>Notes / Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pagedItems.map((row) => (
                  <tr key={row.id} className={String(row.status || '') === 'Cancelled' ? 'collections-cancelled-row' : ''}>
                    <td>{formatDate(row.remittance_date)}</td>
                    <td>{formatDate(row.collection_date)}</td>
                    <td>{row.branch || '-'}</td>
                    <td className="collections-mono-cell">{row.reference_no || '-'}</td>
                    <td className="collections-number">{formatCurrency(row.cash_amount)}</td>
                    <td className="collections-number">{formatCurrency(row.check_amount)}</td>
                    <td className="collections-number">{formatCurrency(row.total_amount)}</td>
                    <td>
                      {row.deposit_slip_url ? (
                        <a href={row.deposit_slip_url} target="_blank" rel="noreferrer">
                          {row.deposit_slip_name || 'View'}
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${String(row.status || '') === 'Cancelled' ? 'status-cancelled' : 'status-active'}`}>
                        {row.status || 'Active'}
                      </span>
                    </td>
                    <td>{String(row.status || '') === 'Cancelled' ? (row.cancellation_reason || '-') : (row.notes || '-')}</td>
                    <td>
                      {String(row.status || '') === 'Cancelled' ? (
                        '-'
                      ) : (
                        <div className="dashboard-actions">
                          {canUpdateRemittance ? (
                            <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={() => openEditModal(row)}>
                              Edit
                            </button>
                          ) : null}
                          {canCancelRemittance ? (
                            <button type="button" className="profile-secondary-btn profile-secondary-btn-xs" onClick={() => setCancelTarget(row)}>
                              Cancel
                            </button>
                          ) : null}
                          {canDeleteRemittance ? (
                            <button type="button" className="profile-danger-btn profile-danger-btn-xs" onClick={() => onDelete(row.id)} disabled={isSubmitting}>
                              Delete
                            </button>
                          ) : null}
                          {!canUpdateRemittance && !canCancelRemittance && !canDeleteRemittance ? <span>-</span> : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading ? <TablePagination {...pagination} /> : null}
      </section>

      <FdModal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingRecord(null); }}
        eyebrow="Collections"
        title={editingRecord ? 'Edit Bank Remittance' : 'New Bank Remittance'}
        subtitle="Track bank deposit details."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => { setIsFormOpen(false); setEditingRecord(null); }} disabled={isSubmitting}>
              Close
            </button>
            <button
              type="button"
              className="fd-btn fd-btn-primary"
              onClick={submitForm}
              disabled={
                isSubmitting
                || isUploadingSlip
                || !form.remittance_date
                || !form.collection_date
                || !form.deposit_slip_url
                || (editingRecord ? !canUpdateRemittance : !canCreateRemittance)
              }
            >
              {isSubmitting ? 'Saving...' : (editingRecord ? 'Save Changes' : 'Save Remittance')}
            </button>
          </>
        )}
      >
        <div className="collections-modal-grid">
          <div className="fd-field"><label>Remittance Date *</label><input type="date" value={form.remittance_date} onChange={(e) => setForm((p) => ({ ...p, remittance_date: e.target.value }))} /></div>
          <div className="fd-field"><label>Collection Date *</label><input type="date" value={form.collection_date} onChange={(e) => setForm((p) => ({ ...p, collection_date: e.target.value }))} /></div>
          <div className="fd-field"><label>Amount Deposited *</label><input type="number" min="0" step="0.01" value={form.cash_amount} onChange={(e) => setForm((p) => ({ ...p, cash_amount: e.target.value }))} /></div>
          <div className="fd-field"><label>Commission Released *</label><input type="number" min="0" step="0.01" value={form.check_amount} onChange={(e) => setForm((p) => ({ ...p, check_amount: e.target.value }))} /></div>
          <div className="fd-field fd-field-wide"><label>Reference No.</label><input value={form.reference_no} onChange={(e) => setForm((p) => ({ ...p, reference_no: e.target.value }))} /></div>
          <div className="fd-field fd-field-wide"><label>Notes</label><textarea className="collections-textarea" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /></div>
          <div className="fd-field fd-field-wide">
            <label>Attach Deposit Slip *</label>
            {form.deposit_slip_url ? (
              <div className="collections-attachment-row">
                <a href={form.deposit_slip_url} target="_blank" rel="noreferrer">
                  {form.deposit_slip_name || 'View deposit slip'}
                </a>
                <button
                  type="button"
                  className="profile-danger-btn profile-danger-btn-xs"
                  onClick={removeDepositSlip}
                  disabled={isUploadingSlip}
                >
                  Remove
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleDepositSlipChange}
                disabled={isUploadingSlip}
              />
            )}
            {isUploadingSlip ? <span className="collections-inline-note">Uploading deposit slip...</span> : null}
            {!form.deposit_slip_url && !isUploadingSlip ? (
              <span className="collections-inline-note">The deposit slip file attachment is required.</span>
            ) : null}
            {uploadError ? <span className="collections-danger">{uploadError}</span> : null}
          </div>
          <div className="collections-rate-preview">
            <strong>Total Deposit</strong>
            <span>{formatCurrency(toNumber(form.cash_amount) + toNumber(form.check_amount))}</span>
          </div>
        </div>
      </FdModal>

      <FdModal
        isOpen={Boolean(cancelTarget)}
        onClose={() => { setCancelTarget(null); setCancelReason(''); }}
        eyebrow="Collections"
        title="Cancel Bank Remittance"
        subtitle="Set cancellation reason and mark as cancelled."
        footer={(
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={() => { setCancelTarget(null); setCancelReason(''); }} disabled={isSubmitting}>
              Close
            </button>
            <button type="button" className="fd-btn fd-btn-primary" onClick={submitCancel} disabled={isSubmitting || !canCancelRemittance || !cancelReason.trim()}>
              {isSubmitting ? 'Saving...' : 'Confirm Cancel'}
            </button>
          </>
        )}
      >
        <div className="collections-modal-grid">
          <div className="fd-field fd-field-wide"><label>Reference No.</label><input value={cancelTarget?.reference_no || ''} readOnly /></div>
          <div className="fd-field fd-field-wide">
            <label>Cancellation Reason *</label>
            <textarea className="collections-textarea" value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} />
          </div>
        </div>
      </FdModal>
    </div>
  );
}
